import { TFunction } from "next-i18next";
import { ArrowRightOutlineIcon } from "@smile2impress/components";
import {
    UTSLocaleToCountryVersion,
    UTSLocaleToLanguage,
} from "@smile2impress/attraction-constants";
import { withCdnUrl } from "@smile2impress/attraction-utils";
import { BaseLayoutContent } from "layouts/relaunch/RelaunchLayout/RelaunchLayout.types";

import { ContactTypes, SocialTypes } from "constants/enum";
import contacts from "configs/contacts";
import { getWALink } from "utils/getWALink";
import { getCurrentYear } from "utils/date";
import { PageContentProps } from "content/content.types";
import {
    OFFERS_AND_DISCOUNTS_LINKS_BY_LOCALE,
    PRIVACY_POLICY_LINKS_BY_LOCALE,
    UrlsConfig,
} from "configs/urlsConfig";

const pageContent = (
    t: TFunction,
    { locale }: PageContentProps
): BaseLayoutContent => ({
    header: {
        menuTitle: t("navigationMenuRelaunch:navigationMenu"),
        logoLink: UrlsConfig.MAIN,
        items: [
            {
                text: t("navigationMenuRelaunch:navigationTreatments"),
                highlighted: true,
                items: [
                    {
                        text: t("navigationMenuRelaunch:navigationAligners"),
                        link: UrlsConfig.ALIGNERS,
                        image: {
                            images: [
                                {
                                    png: withCdnUrl(
                                        "/images/relaunch/common/navigation/aligners.png"
                                    ),
                                },
                            ],
                            defaultSrc: withCdnUrl(
                                "/images/relaunch/common/navigation/aligners.png"
                            ),
                        },
                    },
                    {
                        text: t("navigationMenuRelaunch:navigationTeens"),
                        link: UrlsConfig.TEENS,
                        image: {
                            images: [
                                {
                                    png: withCdnUrl(
                                        "/images/relaunch/common/navigation/teens.png"
                                    ),
                                },
                            ],
                            defaultSrc: withCdnUrl(
                                "/images/relaunch/common/navigation/teens.png"
                            ),
                        },
                    },
                    {
                        text: t("navigationMenuRelaunch:navigationKids"),
                        link: UrlsConfig.KIDS,
                        image: {
                            images: [
                                {
                                    png: withCdnUrl(
                                        "/images/relaunch/common/navigation/kids.png"
                                    ),
                                },
                            ],
                            defaultSrc: withCdnUrl(
                                "/images/relaunch/common/navigation/kids.png"
                            ),
                        },
                    },
                    {
                        text: t("navigationMenuRelaunch:navigationRetainers"),
                        link: UrlsConfig.RETAINERS_SUBSCRIPTION,
                        image: {
                            images: [
                                {
                                    png: withCdnUrl(
                                        "/images/relaunch/common/navigation/retainers.png"
                                    ),
                                },
                            ],
                            defaultSrc: withCdnUrl(
                                "/images/relaunch/common/navigation/retainers.png"
                            ),
                        },
                    },
                ],
            },
            {
                text: t("navigationMenuRelaunch:navigationWhitening"),
                link: UrlsConfig.WHITENING,
                image: {
                    images: [
                        {
                            png: withCdnUrl(
                                "/images/relaunch/common/navigation/whitening.png"
                            ),
                        },
                    ],
                    defaultSrc: withCdnUrl(
                        "/images/relaunch/common/navigation/whitening.png"
                    ),
                },
            },
            {
                text: t("navigationMenuRelaunch:navigationWhy"),
                link: UrlsConfig.WHY_IMPRESS,
                image: {
                    images: [
                        {
                            png: withCdnUrl(
                                "/images/relaunch/common/navigation/whyImpress.png"
                            ),
                        },
                    ],
                    defaultSrc: withCdnUrl(
                        "/images/relaunch/common/navigation/whyImpress.png"
                    ),
                },
            },
            {
                text: t("navigationMenuRelaunch:navigationPricing"),
                link: UrlsConfig.PRICES,
                image: {
                    images: [
                        {
                            png: withCdnUrl(
                                "/images/relaunch/common/navigation/prices.png"
                            ),
                        },
                    ],
                    defaultSrc: withCdnUrl(
                        "/images/relaunch/common/navigation/prices.png"
                    ),
                },
            },
            {
                text: t("navigationMenuRelaunch:navigationClinics"),
                link: UrlsConfig.LOCATIONS,
                image: {
                    images: [
                        {
                            png: withCdnUrl(
                                "/images/relaunch/common/navigation/locations.png"
                            ),
                        },
                    ],
                    defaultSrc: withCdnUrl(
                        "/images/relaunch/common/navigation/locations.png"
                    ),
                },
            },
            {
                text: t("navigationMenuRelaunch:navigationResults"),
                link: UrlsConfig.RESULTS,
                image: {
                    images: [
                        {
                            png: withCdnUrl(
                                "/images/relaunch/common/navigation/results.png"
                            ),
                        },
                    ],
                    defaultSrc: withCdnUrl(
                        "/images/relaunch/common/navigation/results.png"
                    ),
                },
            },
            {
                text: t("navigationMenuRelaunch:navigationBlog"),
                link: UrlsConfig.BLOG,
                image: {
                    images: [
                        {
                            png: withCdnUrl(
                                "/images/relaunch/common/navigation/blog.png"
                            ),
                        },
                    ],
                    defaultSrc: withCdnUrl(
                        "/images/relaunch/common/navigation/blog.png"
                    ),
                },
            },
        ],
        cta: {
            text: t("navigationMenuRelaunch:navigationCTA"),
            link: UrlsConfig.UNIVERSAL_FORM,
        },
        ctaExpanded: {
            text: t("navigationMenuRelaunch:navigationCTA2"),
            link: UrlsConfig.UNIVERSAL_FORM,
            iconRight: ArrowRightOutlineIcon,
        },
        login: {
            text: "",
            link: `${process.env.PERSONAL_AREA_URL}/login`,
        },
        candidate: {
            text: t("navigationMenuRelaunch:navigationQuiz"),
            link: UrlsConfig.CANDIDATE,
        },
    },
    contacts: {
        wa: {
            text: t("navigationMenuRelaunch:navigationWhatsApp"),
            link: contacts.whatsapp[locale].link,
            message: t("global:whatsappMessage"),
            ...((contacts.whatsapp[locale] as any).newPatientsLink
                ? {
                      newPatientsLink: getWALink(
                          (contacts.whatsapp[locale] as any).newPatientsLink,
                          t("global:whatsappMessage")
                      ),
                  }
                : {}),
        },
    },
    footer: {
        company: {
            title: t("global:companyFooterText"),
            links: [
                {
                    id: "whyImpress",
                    name: t("global:whyImpressPageTitle"),
                    link: UrlsConfig.WHY_IMPRESS,
                },
                {
                    id: "press",
                    name: t("global:pressPageTitle"),
                    link: `mailto:${contacts.emailPress.link}`,
                },
                {
                    id: "careers",
                    name: t("global:careersPageTitle"),
                    link: "https://www.linkedin.com/company/impress-orthodontics/jobs/",
                    target: "_blank",
                },
                {
                    id: "blog",
                    name: t("global:blogPageTitle"),
                    link: UrlsConfig.BLOG,
                    target: "_self",
                },
                {
                    id: "offersAndDiscounts",
                    name: t("navigationMenuRelaunch:offersAndDiscounts"),
                    link: OFFERS_AND_DISCOUNTS_LINKS_BY_LOCALE[locale],
                },
            ],
        },
        product: {
            title: t("global:productFooterText"),
            links: [
                {
                    id: "aligners",
                    name: t("global:alignersPageTitle"),
                    link: UrlsConfig.ALIGNERS,
                },
                {
                    id: "teens",
                    name: t("global:teensPageTitle"),
                    link: UrlsConfig.TEENS,
                },
                {
                    id: "kids",
                    name: t("global:kidsPageTitle"),
                    link: UrlsConfig.KIDS,
                },
                {
                    id: "retainers",
                    name: t("global:nightRetainersPageTitle"),
                    link: UrlsConfig.RETAINERS_SUBSCRIPTION,
                },
                {
                    id: "whitening",
                    name: t("global:whiteningPageTitle"),
                    link: UrlsConfig.WHITENING,
                },
                {
                    id: "prices",
                    name: t("global:pricePageTitle"),
                    link: UrlsConfig.PRICES,
                },
                {
                    id: "results",
                    name: t("global:resultsPageTitle"),
                    link: UrlsConfig.RESULTS,
                },
            ],
        },
        legal: {
            title: t("global:legalNoticeFooterTitle"),
            links: [
                {
                    id: "privacyPolicyPage",
                    name: t("global:privacyPolicy"),
                    link: PRIVACY_POLICY_LINKS_BY_LOCALE[locale],
                },
                {
                    id: "cookiesPage",
                    name: t("global:cookiesPolicy"),
                    link: withCdnUrl(
                        `${UrlsConfig.POLICY}/cookies-policy_${UTSLocaleToCountryVersion[locale]}.pdf`
                    ),
                },
                {
                    id: "legalNoticePage",
                    name: t("global:legalNoticeFooterText"),
                    link: withCdnUrl(
                        `${UrlsConfig.POLICY}/aviso-legal_${UTSLocaleToCountryVersion[locale]}.pdf`
                    ),
                },
            ],
        },
        support: {
            title: t("global:supportFooterText"),
            links: [
                {
                    id: "faq",
                    name: t("global:faqFooterText"),
                    link: `https://faq.smile2impress.com/${UTSLocaleToLanguage[locale]}`,
                },
                {
                    id: ContactTypes.PHONE,
                    name: contacts.phone[locale].public,
                    link:
                        contacts.phone[locale].link &&
                        `tel:${contacts.phone[locale].link}`,
                },
                {
                    id: SocialTypes.WA,
                    name: "WhatsApp",
                    link: getWALink(
                        contacts.whatsapp[locale].link,
                        t("global:whatsappMessage")
                    ),
                },
                {
                    id: SocialTypes.TG,
                    name: "Telegram",
                    link: contacts.telegram[locale].link,
                },
                {
                    id: ContactTypes.EMAIL,
                    name: t("global:email"),
                    link:
                        contacts.email[locale].link &&
                        `mailto:${contacts.email[locale].link}`,
                },
            ],
        },
        disclaimerContent: t("global:disclaimerContent"),
        copyright: {
            text: `© ${getCurrentYear()} SMILE2IMPRESS SL. ${t(
                "global:copyright"
            )}`,
        },
        socialNetworks: [
            {
                iconName: SocialTypes.INSTAGRAM,
                link: contacts.instagram[locale].link,
            },
            {
                iconName: SocialTypes.FB,
                link: contacts.facebook[locale].link,
            },
            {
                iconName: SocialTypes.LINKDIN,
                link: contacts.linkdin[locale].link,
            },
            {
                iconName: SocialTypes.TIKTOK,
                link: contacts.tiktok[locale].link,
            },
        ],
        workingHours: {
            weekdays: t("global:workingHours.weekdays"),
            weekends: t("global:workingHours.weekends"),
        },
    },
});

export default pageContent;
